import { useState, useEffect, useRef, useCallback } from "react";
import { PaymentStepComponentProps } from "../PaymentMethodStepInterface.ts";
import { useCreateHeyLightOrder } from "../../../HeyLight/useCreateHeyLightOrder.ts";
import { HeyLightCheckoutFormData } from "../../../HeyLight/HeyLightCheckoutForm.tsx";
import { FullPageLoadingWithMessage } from "../../../UI/FullPageLoadingWithMessage.tsx";

export function HeyLightPaymentStep({
  checkoutData,
  itemType,
  item,
  analyticsParams,
  onError,
}: PaymentStepComponentProps) {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { createHeyLightOrder } = useCreateHeyLightOrder();
  const orderCreated = useRef(false);

  const handleError = useCallback(
    (message: string) => {
      onError(message);
      setIsRedirecting(false);
    },
    [onError],
  );

  useEffect(() => {
    if (orderCreated.current) {
      return;
    }

    const initHeyLight = async () => {
      orderCreated.current = true;
      setIsRedirecting(true);

      try {
        const heyLightData: HeyLightCheckoutFormData = {
          email: checkoutData.customer.email,
          name: checkoutData.customer.firstName,
          surname: checkoutData.customer.lastName,
          phoneNumber: {
            countryCode: checkoutData.countryCode || "IT",
            number: checkoutData.number || "",
            dialCode: checkoutData.dialCode || "+39",
          },
          invoiceRequired: checkoutData.invoiceRequired,
          businessType: checkoutData.businessType,
          pec: checkoutData.pec,
          sdiCode: checkoutData.sdiCode,
          vat: checkoutData.vat,
          privacyPolicyAccepted: checkoutData.privacyPolicyAccepted,
          additionalTerms: checkoutData.additionalTerms,
          participants: checkoutData.participants.map((p) => ({
            firstName: p.firstName,
            lastName: p.lastName,
            email: p.email,
          })),
          bundleParticipants: checkoutData.bundleParticipants,
          coupon: checkoutData.coupon,
          promos: checkoutData.promos,
        };

        const { order } = await createHeyLightOrder(
          heyLightData,
          item._id,
          analyticsParams,
          true,
          itemType,
        );

        setIsRedirecting(false);
        window.location.href = order.heyLightInformation.heyLightRedirectUrl;
      } catch (err) {
        console.error("Error creating HeyLight order:", err);
        orderCreated.current = false;
        handleError(
          "Si è verificato un errore durante la creazione dell'ordine. Riprova più tardi.",
        );
      }
    };

    initHeyLight();
  }, [
    checkoutData,
    item._id,
    itemType,
    analyticsParams,
    handleError,
    createHeyLightOrder,
  ]);

  return (
    <FullPageLoadingWithMessage
      mainMessage={"Reindirizzamento al servizio di finanziamento"}
      secondaryMessage={
        isRedirecting
          ? "Stiamo preparando il tuo ordine e ti reindirizzeremo alla pagina di HeyLight per completare il pagamento..."
          : "Preparazione in corso..."
      }
    />
  );
}
